import { useBalance, useNodeById } from '@/stores/analysisStore';
import { analysisActions } from '@/stores/analysisStore';
import BalanceForm from './BalanceForm';
import { Fragment, useCallback, useEffect } from 'react';
import AnalysisBox from '@/components/core/AnalysisBox';
import { numberFormat } from '@/utils/unitUtils';
import { getSelectionRootNodeId } from '@/utils/balanceUtils';
import { usePlotSync, usePlotSyncHandler } from '@/components/providers/PlotSyncProvider';

const Balance = () => {
  const plot = useBalance();
  const { nodeId, results } = plot;
  // an internal pathway node is not in the store's nodes, so resolve the
  // top-level node its selection belongs to
  const node = useNodeById(getSelectionRootNodeId(nodeId));
  const { setParams } = usePlotSync();

  const updatePlot = useCallback(async formData => {
    // sibling plots only understand top-level nodes, so share the node an
    // internal pathway selection belongs to rather than the selection itself
    setParams({ ...formData, nodeId: getSelectionRootNodeId(formData.nodeId) });
    await analysisActions.updatePlot('balance', formData);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { syncData } = usePlotSyncHandler(updatePlot, plot);

  useEffect(() => {
    syncData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!node || !results) {
    return null;
  }

  return (
    <div className="relative">
      <BalanceForm key={plot.nodeId} onSubmit={updatePlot} defaultValues={plot} />
      <AnalysisBox>
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full bg-white">
            <tbody>
              {results?.map(({ header, rows }, sectionIndex) => {
                const isFirstSection = sectionIndex === 0;
                return (
                  <Fragment key={header}>
                    {/* Section Header */}
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <td
                        className="px-4 py-2 text-sm font-semibold text-gray-800"
                        dangerouslySetInnerHTML={{ __html: header }}
                      />
                      {isFirstSection && (
                        <>
                          <td className="px-4 py-2 text-right text-sm font-semibold text-gray-800">Quantity</td>
                          <td className="px-4 py-2 text-left text-sm font-semibold text-gray-800">Units</td>
                        </>
                      )}
                      {!isFirstSection && <td colSpan={2}></td>}
                    </tr>

                    {/* Section Rows */}
                    {rows.map(({ label, quantity, unit }, rowIndex) => (
                      <tr
                        key={`${header}-${label}`}
                        className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50/50 transition-colors duration-150`}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{label}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-700">
                          {quantity && numberFormat(quantity, 2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{unit}</td>
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </AnalysisBox>
    </div>
  );
};

export default Balance;
